/**
 * summerGates — port littéral du sous-ensemble de `@common/utils/summer2026` dont le portail
 * Abonnement a besoin **pour ses gardes**.
 *
 * Le cockpit Été lui-même n'est pas porté (bandeau et récap restent legacy, hors MVP), mais ses
 * prédicats décident de choses qui, elles, sont portées : la carte « Renouvellement à venir » est
 * masquée pendant la fenêtre d'été, la ligne « Fin d'engagement » l'est quand l'échéance tombe
 * dans la fenêtre gérée par le cockpit, et la carte « Et ensuite ? » s'efface quand le choix
 * d'été est entériné. Les recopier était le seul moyen de garder ces trois gardes à l'identique
 * sans importer `src/common/**` depuis les ports.
 *
 * Symboles legacy repris : `getSummerConfigSafe`, `SUMMER_CHOICE_STATUS`,
 * `isConfirmedSummerStatus`, `isSummer2026FlagForced`, `isSummer2026Window`,
 * `isSummer2026Visible`, `getSummerJuneBounds`, `isCommitmentInSummerCockpitWindow`,
 * `resolveDisplayResumeDate`.
 */
import moment from "moment"

/**
 * Le payload `staticData.summer_config`, tel que le back le sert. Toutes les clés optionnelles, et
 * toutes nullables : selon la porte (lien d'activation, `/api/user`, `staticData`) une clé absente
 * arrive tantôt manquante, tantôt à `null`. Les deux passent par le même `??`.
 */
export type ApiSummerConfig = {
    current_year?: number | null
    cockpit_open?: string | null
    window_start?: string | null
    window_end?: string | null
    cockpit_accessible_until?: string | null
    resume_date?: string | null
    resume_window_end?: string | null
    engagement_tolerance_end?: string | null
    post_window_grace_days?: number | null
    cockpit_enabled?: boolean | null
}

export type SummerConfig = {
    year: number
    cockpitOpen: string
    cockpitClose: string
    windowStart: string
    windowEnd: string
    resumeDate: string
    cockpitEnabled: boolean
}

/** Dates de l'été, servies par le back quand elles existent, retombées sur l'année en cours sinon. */
export function getSummerConfigSafe(summerConfig: ApiSummerConfig | null | undefined): SummerConfig {
    const year = summerConfig?.current_year ?? moment().year()
    const cockpitOpen = summerConfig?.cockpit_open ?? `${year}-05-15`
    const windowStart = summerConfig?.window_start ?? `${year}-07-01`
    const windowEnd = summerConfig?.window_end ?? `${year}-08-31`
    const cockpitClose = summerConfig?.cockpit_accessible_until ?? windowEnd
    const resumeDate = summerConfig?.resume_date ?? moment(windowEnd).add(1, "day").format("YYYY-MM-DD")
    // Un payload qui ne porte pas le champ garde l'ancien comportement : seul un `false`
    // explicite éteint la surface parent du cockpit.
    const cockpitEnabled = summerConfig?.cockpit_enabled !== false

    return { year, cockpitOpen, cockpitClose, windowStart, windowEnd, resumeDate, cockpitEnabled }
}

export const SUMMER_CHOICE_STATUS = {
    NOT_VISITED: "not_visited",
    VIEWED_NO_CHOICE: "viewed_no_choice",
    CHOSE_SUMMER_COURSES: "chose_summer_courses",
    CHOSE_AUTONOMY: "chose_autonomy",
    CHOSE_PAUSE: "chose_pause",
    AUTO_PAUSED: "auto_paused",
} as const

/** Le back a-t-il un choix d'été entériné ? */
export function isConfirmedSummerStatus(status: string | null | undefined): boolean {
    return (
        [
            SUMMER_CHOICE_STATUS.CHOSE_SUMMER_COURSES,
            SUMMER_CHOICE_STATUS.CHOSE_AUTONOMY,
            SUMMER_CHOICE_STATUS.CHOSE_PAUSE,
            SUMMER_CHOICE_STATUS.AUTO_PAUSED,
        ] as string[]
    ).includes(status ?? "")
}

/* global process */
const FLAG_ENV =
    typeof process !== "undefined" && process && process.env ? process.env.EXPO_PUBLIC_SUMMER_2026_ENABLED : undefined

export function isSummer2026FlagForced(): boolean {
    return FLAG_ENV === "true" || FLAG_ENV === "1"
}

export function isSummer2026Window(now: moment.Moment, summerConfig: ApiSummerConfig | null | undefined): boolean {
    const { cockpitOpen, cockpitClose } = getSummerConfigSafe(summerConfig)
    return now.isBetween(moment(cockpitOpen), moment(cockpitClose).endOf("day"), undefined, "[]")
}

/**
 * Gate UNIQUE de la surface parent du cockpit d'été. L'interrupteur saisonnier passe AVANT la
 * fenêtre de dates et avant le forçage local : une extinction s'impose à tout le monde.
 */
export function isSummer2026Visible(
    now: moment.Moment | undefined,
    summerConfig: ApiSummerConfig | null | undefined
): boolean {
    const { cockpitEnabled } = getSummerConfigSafe(summerConfig)
    if (!cockpitEnabled) return false
    return isSummer2026FlagForced() || isSummer2026Window(now ?? moment(), summerConfig)
}

/**
 * Les bornes de juin, le mois que le cockpit a absorbé : `juneStart` est le premier jour qu'il
 * gère (1ᵉʳ juin = ouverture de fenêtre − 1 mois), `juneEnd` le dernier avant l'été (30 juin).
 * Port littéral de `getSummerJuneBounds` (`@common/utils/summer2026.js:1323-1332`) — le parcours
 * de pause s'en sert pour plafonner une pause manuelle à la veille du cockpit.
 */
export function getSummerJuneBounds(summerConfig: ApiSummerConfig | null | undefined): {
    juneStart: moment.Moment
    juneEnd: moment.Moment
} {
    const { windowStart } = getSummerConfigSafe(summerConfig)
    const ws = moment(windowStart).startOf("day")
    return { juneStart: ws.clone().subtract(1, "month"), juneEnd: ws.clone().subtract(1, "day") }
}

/** Fenêtre gérée par le cockpit pour la règle de masquage de « Fin d'engagement » : 1ᵉʳ juin → 31 août. */
export function isCommitmentInSummerCockpitWindow(
    commitmentEndsAt: unknown,
    summerConfig: ApiSummerConfig | null | undefined
): boolean {
    if (!commitmentEndsAt) return false
    const end = moment(commitmentEndsAt as string)
    if (!end.isValid()) return false
    const { windowEnd, windowStart } = getSummerConfigSafe(summerConfig)
    const juneStart = moment(windowStart).startOf("day").subtract(1, "month")
    return end.isBetween(juneStart.clone().startOf("day"), moment(windowEnd).endOf("day"), undefined, "[]")
}

/**
 * Jour de reprise EFFECTIF — la seule date que l'écran annonce. Ordre de vérité : le jalon posé,
 * puis l'arbitrage du back, puis la pause vivante, puis le choix d'été, puis le défaut global.
 */
export function resolveDisplayResumeDate(args: {
    plannedResume?: unknown
    resolved?: unknown
    pauseResume?: unknown
    summerChoice?: unknown
    summerConfig?: ApiSummerConfig | null
}): moment.Moment | null {
    const { plannedResume = null, resolved = null, pauseResume = null, summerChoice = null, summerConfig = null } = args
    const { resumeDate: globalResume } = getSummerConfigSafe(summerConfig)
    const candidate = [plannedResume, resolved, pauseResume, summerChoice, globalResume].find(
        (value) => value && moment(value as string).isValid()
    )

    return candidate ? moment(candidate as string).startOf("day") : null
}
